'use client';

import { useI18n } from '@/components/I18nProvider';
import { Footer } from '@/components/Footer';
import { Shield } from 'lucide-react';

const LAST_UPDATED_BY_LOCALE = {
  en: 'June 11, 2026',
  es: '11 de junio de 2026',
  ja: '2026年6月11日',
} as const;

type SectionDef =
  | { num: string; titleKey: string; descKey: string }
  | { num: string; titleKey: string; descKey: string; listKeys: string[] };

const sections: SectionDef[] = [
  { num: '01', titleKey: 's01Title', descKey: 's01Desc', listKeys: ['s01L1', 's01L2', 's01L3', 's01L4', 's01L5'] },
  { num: '02', titleKey: 's02Title', descKey: 's02Desc', listKeys: ['s02L1', 's02L2', 's02L3', 's02L4', 's02L5'] },
  { num: '03', titleKey: 's03Title', descKey: 's03Desc' },
  { num: '04', titleKey: 's04Title', descKey: 's04Desc', listKeys: ['s04L1', 's04L2', 's04L3', 's04L4'] },
  { num: '05', titleKey: 's05Title', descKey: 's05Desc' },
  { num: '06', titleKey: 's06Title', descKey: 's06Desc' },
  { num: '07', titleKey: 's07Title', descKey: 's07Desc', listKeys: ['s07L1', 's07L2', 's07L3', 's07L4'] },
  { num: '08', titleKey: 's08Title', descKey: 's08Desc' },
  { num: '09', titleKey: 's09Title', descKey: 's09Desc' },
  { num: '10', titleKey: 's10Title', descKey: 's10Desc' },
];

const fallbacks: Record<string, string> = {
  title: 'Privacy',
  titleHighlight: 'Policy',
  lastUpdated: 'Last updated: {date}',
  s01Title: 'Information We Collect',
  s01Desc: 'To provide a secure, fully authenticated platform, we collect information you provide directly to us when you create an account, link third-party services, upload gaming clips, or contact us.',
  s01L1: 'Account Information: Username, email address, and secure authentication tokens.',
  s01L2: 'Gaming Data: Gaming profiles, gamertags, and connected platform IDs.',
  s01L3: 'Content: Gaming clips, videos, and media you upload to your profile.',
  s01L4: 'Communications: Records of messages and interactions with other users.',
  s01L5: 'Technical Data: IP address, device identifiers, operating system, and app activity logs.',
  s02Title: 'How We Use Your Information',
  s02Desc: 'We use the collected information to provide, maintain, and improve our services, specifically:',
  s02L1: 'To manage your account and maintain a verified user base.',
  s02L2: 'To enable clip-based matchmaking and connect you with other gamers.',
  s02L3: 'To facilitate the sharing of gaming content within Gamerplug and to external platforms.',
  s02L4: 'To send notifications about matches, groups, and messages.',
  s02L5: 'To improve app performance, ensure stability, and provide customer support.',
  s03Title: 'Third-Party Services and APIs',
  s03Desc: 'If you choose to link your Gamerplug account with third-party platforms or use their authentication services, we may receive and store data from those services in accordance with their respective policies.',
  s04Title: 'Information Sharing',
  s04Desc: 'We do not sell, trade, or transfer your personal information to third parties for their own commercial use. We only share information:',
  s04L1: 'With your explicit consent or at your direction (such as sharing a clip to a connected social network).',
  s04L2: 'To comply with valid legal obligations.',
  s04L3: 'To protect our rights, prevent fraud, and ensure the safety of our users.',
  s04L4: 'With trusted service providers who assist in operating our platform, under strict confidentiality agreements.',
  s05Title: 'Data Security',
  s05Desc: 'We implement rigorous security measures to protect your personal information against unauthorized access, alteration, or destruction. We continuously monitor our infrastructure to protect the integrity of our community, though no internet transmission is completely secure.',
  s06Title: 'Data Retention',
  s06Desc: 'We retain your personal information only for as long as your account is active or as needed to provide our services. Once an account is deleted, all associated personal data and gaming clips are permanently removed from our active servers within 30 days, unless a longer retention period is legally required.',
  s07Title: 'Your Rights and Data Deletion',
  s07Desc: 'You maintain full control over your data. You have the right to:',
  s07L1: 'Access, update, or correct your personal information.',
  s07L2: 'Delete Your Data: You can request complete deletion of your account and all associated data by contacting our support team.',
  s07L3: 'Revoke third-party API access and opt-out of marketing communications.',
  s07L4: 'Export your data in a readable format.',
  s08Title: 'Children\'s Privacy',
  s08Desc: 'Our service is not intended for children under 17 years of age, and we do not knowingly collect personal information from children under 17. If we become aware that we have collected such information, we will immediately take steps to delete it.',
  s09Title: 'Changes to This Policy',
  s09Desc: 'We may update this privacy policy periodically. We will notify you of any material changes by posting the new policy in the app and updating the "Last Updated" date at the top of this page.',
  s10Title: 'Contact Us',
  s10Desc: 'If you have any questions or wish to exercise your data rights, please contact us at Support@gamerplug.app.',
};

function getT(privacy: Record<string, string> | undefined, key: string): string {
  return privacy?.[key] || fallbacks[key] || key;
}

export default function LocalizedPrivacy() {
  const { locale = 'en', t } = useI18n();
  const privacy = t.privacy as Record<string, string> | undefined;

  const lastUpdated = getT(privacy, 'lastUpdated').replace('{date}', LAST_UPDATED_BY_LOCALE[locale as keyof typeof LAST_UPDATED_BY_LOCALE] || LAST_UPDATED_BY_LOCALE.en);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary selection:text-white">
      {/* Background Elements */}
      <div className="fixed top-0 left-0 w-full h-full -z-10 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[700px] h-[700px] bg-accent/10 rounded-full blur-[120px]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#202020_1px,transparent_1px),linear-gradient(to_bottom,#202020_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20"></div>
      </div>

      <div className="container mx-auto px-6 py-32 max-w-5xl relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-secondary rounded-2xl flex items-center justify-center border border-border shadow-lg">
              <Shield size={40} className="text-primary" />
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 uppercase italic tracking-tight">
            {getT(privacy, 'title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              {getT(privacy, 'titleHighlight')}
            </span>
          </h1>
          <p className="text-muted-foreground text-lg border-l-2 border-border pl-6 inline-block">
            {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-12">
          {sections.map((sec) => (
            <section
              key={sec.num}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <h2 className="text-3xl font-bold mb-6 text-foreground relative z-10 uppercase tracking-tight">
                <span className="text-primary">{sec.num}.</span> {getT(privacy, sec.titleKey)}
              </h2>
              <p className={`text-muted-foreground leading-relaxed relative z-10${'listKeys' in sec ? ' mb-6' : ''}`}>
                {getT(privacy, sec.descKey)}
              </p>
              {'listKeys' in sec && (
                <ul className="space-y-3 relative z-10">
                  {sec.listKeys.map((lk) => (
                    <li key={lk} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-muted-foreground">{getT(privacy, lk)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
